import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
};

const panel = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: { opacity: 0, y: 24, scale: 0.98, transition: { duration: 0.2 } },
};

const MarketingOfferModal = ({ open, product, onClose, onViewProducts }) => {
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          variants={backdrop}
          initial="hidden"
          animate="show"
          exit="hidden"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8"
        >
          <motion.div
            variants={panel}
            initial="hidden"
            animate="show"
            exit="exit"
            className="w-full max-w-2xl rounded-[32px] bg-white p-6 shadow-2xl shadow-slate-900/30"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--brand-blue)]">
                  Marketing Offer
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Ready for your first coverage?
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Secure your access to emergency care by choosing the starter
                  package below.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-100 bg-slate-50/80 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h4 className="text-xl font-semibold text-slate-900">
                    {product?.title || "Emergency Care Package"}
                  </h4>
                  {product?.secondary_title && (
                    <p className="mt-1 text-sm text-slate-500">
                      {product.secondary_title}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-semibold text-[var(--brand-blue)]">
                    {product?.amount ? `PHP ${Number(product.amount).toFixed(2)}` : "PHP 0.00"}
                  </p>
                  {product?.validity_days && (
                    <span className="mt-2 inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-[var(--brand-blue)]">
                      {product.validity_days} Days Validity
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4">
                {product?.product_image ? (
                  <img
                    src={product.product_image}
                    alt={product.title}
                    className="h-56 w-full rounded-2xl border border-slate-200 object-contain bg-white"
                  />
                ) : (
                  <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white text-sm text-slate-400">
                    No product image yet.
                  </div>
                )}
              </div>

            </div>

            <div className="mt-6 flex flex-wrap justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsBenefitsOpen(true)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Medical Benefits
              </button>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-2xl border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={onViewProducts}
                  className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  View Products
                </button>
              </div>
            </div>

            <AnimatePresence>
              {isBenefitsOpen && (
                <motion.div
                  variants={backdrop}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8"
                >
                  <motion.div
                    variants={panel}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    className="w-full max-w-xl rounded-[28px] bg-white p-6 shadow-2xl shadow-slate-900/30"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400">
                          Marketing Offer
                        </p>
                        <h4 className="mt-2 text-2xl font-semibold text-slate-900">
                          Medical Benefits
                        </h4>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsBenefitsOpen(false)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
                      >
                        Close
                      </button>
                    </div>

                    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      {product?.description ? (
                        <p className="whitespace-pre-line">{product.description}</p>
                      ) : (
                        <p>No benefits available.</p>
                      )}
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setIsBenefitsOpen(false)}
                        className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                      >
                        Got it
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MarketingOfferModal;
